import React, { useState, useEffect, useRef } from 'react';
import { Container, Alert } from 'react-bootstrap';
import { useAuth } from '../../../AuthContext/AuthContext';
import { useTranslation } from 'react-i18next';
import StaffInformationCombined from './StaffInformationCombined';
import { fetchStaffInformation, saveStaffInformation } from './StaffInformationActions';
import { sanitizeForm } from '../../../Utilities/DataCleanUp';
import { REQUIRED_FIELDS } from '../../MainForm/config/formConfig';

const StaffInformationForm = ({ onSave, onValidationChange, data }) => {
    const { t } = useTranslation('staffInformation');
    const { currentUser } = useAuth();
    const [staffData, setStaffData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [attemptedSave, setAttemptedSave] = useState(false);
    const fetchedRef = useRef(false);

    // Default empty staff data
    const emptyStaffData = {
        gender: '',
        nationality: '',
        index_number: '',
        un_id: '',
        un_id_expiration_date: '',
        staff_status: '',
        dependants: 0,
        date_of_birth: '',
    };

    // Fetch staff information on mount
    useEffect(() => {
        const loadData = async () => {
            if (!currentUser?.id || fetchedRef.current) return;
            try {
                setLoading(true);
                const data = await fetchStaffInformation();
                if (data) {
                    console.log("Loaded staff data:", data);
                    setStaffData(data);
                    // Only validate silently to update progress
                    validateAndUpdateProgress(data, false);
                } else {
                    // If no data returned, use the empty template
                    setStaffData(emptyStaffData);
                }
                fetchedRef.current = true;
            } catch (err) {
                console.error("Error loading staff data:", err);
                setError(t('staffInformationForm.alerts.loadingError'));
                // Even on error, set some default data to prevent null
                setStaffData(emptyStaffData);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [currentUser?.id, t]);

    // Validate form data and update progress
    const validateAndUpdateProgress = (data, showErrors = false) => {
        const requiredFields = REQUIRED_FIELDS.staff_info;
        let isValid = true;
        const errors = {};

        // Check required fields
        Object.entries(requiredFields).forEach(([field, weight]) => {
            const value = data[field];
            if (weight === 1.0 && (!value || value.toString().trim() === '')) {
                isValid = false;
                errors[field] = t('staffInformationForm.errors.fieldRequired', { field: field.replace(/_/g, ' ') });
            }
        });

        // Validate date of birth for 18+ years
        if (data.date_of_birth) {
            const birthDate = new Date(data.date_of_birth);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            // Adjust age if birthday hasn't occurred this year
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                const actualAge = age - 1;
                if (actualAge < 18) {
                    isValid = false;
                    errors.date_of_birth = t('staffInformationForm.errors.dateOfBirthUnderage');
                }
            } else if (age < 18) {
                isValid = false;
                errors.date_of_birth = t('staffInformationForm.errors.dateOfBirthUnderage');
            }
        }

        // Calculate percentage completion explicitly
        let completedWeight = 0;
        let totalWeight = 0;

        Object.entries(requiredFields).forEach(([field, weight]) => {
            totalWeight += weight;
            const value = data[field];
            if (value && value.toString().trim() !== '') {
                completedWeight += weight;
                console.log(t('validation.fieldComplete', { field, value }));
            } else {
                console.log(t('validation.fieldIncomplete', { field }));
            }
        });

        // Calculate percentage (0 to 100)
        const percentage = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;
        console.log(t('validation.progressCalculation', {
            completed: completedWeight,
            total: totalWeight,
            percentage
        }));

        if (showErrors) {
            setValidationErrors(errors);
        }

        // Pass the actual percentage to parent component
        if (typeof onValidationChange === 'function') {
            console.log(t('validation.callingValidationChange', { percentage }));
            onValidationChange(percentage);
        }

        return { isValid, errors, percentage };
    };

    const handleFieldChange = (name, value) => {
        // Update the staff data directly without relying on child component state
        setStaffData(prevData => {
            const updatedData = { ...prevData, [name]: value };

            // Calculate and update the progress immediately after field change
            const { percentage } = validateAndUpdateProgress(updatedData, false);
            console.log(t('validation.updatedPercentageAfterChange', { percentage }));

            // If we've already attempted to save, re-validate this field
            if (attemptedSave) {
                // Create a temporary validation result for just this field
                const { isValid, errors } = validateAndUpdateProgress(updatedData, false);

                // Update only this field's validation status
                if (!errors[name]) {
                    setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[name];
                        return newErrors;
                    });
                } else {
                    setValidationErrors(prev => ({
                        ...prev,
                        [name]: errors[name]
                    }));
                }
            }

            return updatedData;
        });
    };

    const handleSave = async (formDataToSave) => {
        try {
            setAttemptedSave(true);
            setSaving(true);
            setError(null);

            const dataToSave = { ...staffData };
            console.log("Saving staff data:", dataToSave);

            const sanitizedData = sanitizeForm({
                ...dataToSave,
                user_id: currentUser.id,
            });

            // Validate before sending
            const { isValid, errors, percentage } = validateAndUpdateProgress(sanitizedData, true);
            console.log(t('validation.validationBeforeSave', { isValid, percentage }));

            if (!isValid) {
                setSaving(false);
                setError(t('staffInformationForm.alerts.validationError'));
                return null;
            }

            const savedData = await saveStaffInformation(sanitizedData, currentUser);
            console.log("Saved staff data response:", savedData);

            // Update local state with saved data
            if (savedData) {
                const mergedData = { ...dataToSave, ...savedData };
                setStaffData(mergedData);
            }

            setSuccess(true);
            setValidationErrors({});
            setAttemptedSave(false);
            setTimeout(() => setSuccess(false), 5000);

            // Call parent onSave callback FIRST with the CORRECT SECTION NAME
            console.log("Calling onSave with 'staff_info':", savedData);
            if (onSave && savedData) {
                onSave(savedData);
            }

            // Then calculate percentage again
            console.log(t('validation.recalculatingAfterSave'));
            // Do NOT call validateAndUpdateProgress again, as it triggers another onValidationChange
            // Instead, update the progress through FormContent's regular mechanism

            return savedData;
        } catch (error) {
            console.error('Error saving staff information:', error);
            setError(t('staffInformationForm.alerts.saveError'));
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Prepare a list of validation error messages
    const getValidationErrorMessages = () => {
        return Object.entries(validationErrors).map(([field, message]) => (
            `• ${message}`
        ));
    };

    return (
        <Container className="mt-4 mb-5">
            <h2 className="text-center mb-4" style={{ fontWeight: 'bold', color: '#4682B4' }}>
                {t('staffInformationForm.pageTitle')}
            </h2>

            {loading && <Alert variant="info" className="text-center">{t('staffInformationForm.alerts.loadingInfo')}</Alert>}
            {saving && <Alert variant="info" className="text-center">{t('staffInformationForm.alerts.savingInfo')}</Alert>}

            {success && (
                <Alert variant="success" className="text-center">
                    <strong>{t('status.success', { ns: 'common' })}!</strong> {t('staffInformationForm.alerts.saveSuccess')}
                </Alert>
            )}

            {/* Always show the staff form when not in initial loading state */}
            {!loading && staffData && (
                <StaffInformationCombined
                    formData={staffData}
                    currentUser={currentUser}
                    onSave={handleSave}
                    onFieldChange={handleFieldChange}
                    isSaving={saving}
                    validationErrors={validationErrors}
                    attemptedSave={attemptedSave}
                />
            )}
        </Container>
    );
};

export default StaffInformationForm;